import { Link } from '@tanstack/react-router';
import { Repeat } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type {
  SchedulePlacementState,
  StoreProductSchedule,
} from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { cn } from '@nucleus-commerce/ui/lib/utils';

const STYLES: Record<SchedulePlacementState, string> = {
  scheduled: 'border-border text-foreground',
  live: 'bg-success/15 text-success border-transparent dark:bg-success/20',
  offline: 'border-border text-muted-foreground',
};

/** The rotation's own page; a click inside a clickable table row stays on the link. */
export function ScheduleLink({
  schedule,
  className,
}: {
  schedule: Pick<StoreProductSchedule, 'id' | 'name'>;
  className?: string;
}) {
  return (
    <Link
      to="/org/schedules/$scheduleId"
      params={{ scheduleId: schedule.id }}
      className={cn('truncate hover:underline', className)}
      onClick={(event) => event.stopPropagation()}
    >
      {schedule.name}
    </Link>
  );
}

/**
 * Where a rotation has this store product: "Scheduled" until its first run, "Rotation live",
 * "Rotated out". `withName` adds the rotation's name, linking to it.
 */
export function ScheduleOwnerBadge({
  schedule,
  withName = false,
}: {
  schedule: StoreProductSchedule;
  withName?: boolean;
}) {
  const { t } = useTranslation('storeCatalog');
  const badge = (
    <Badge
      variant="outline"
      className={cn('gap-1 font-normal', STYLES[schedule.state])}
      title={t('owner.badgeTitle', { name: schedule.name })}
    >
      <Repeat className="size-3" aria-hidden />
      {t(`scheduleState.${schedule.state}`)}
    </Badge>
  );
  if (!withName) return badge;
  return (
    <span className="flex min-w-0 flex-col items-start gap-0.5">
      {badge}
      <ScheduleLink schedule={schedule} className="text-muted-foreground max-w-full text-xs" />
    </span>
  );
}
